import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Grid } from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { postAPICall, getAPICall, putAPICall, deleteAPICall } from "lib/callApi";
import {
  FETCH_FUNNEL_DATA,
  SAVE_CONVERSION_FUNNEL,
  SAVE_CONVERSION_FUNNEL_AS,
  GET_FUNNEL_ACCESS_LIST_OPTIONS,
  UPDATE_CONVERSION_FUNNEL,
  USER_CONFIGS,
  GET_FUNNEL_BY_ID,
  UPDATE_CONVERSION_FUNNEL_ACCESS_LIST,
  GET_FUNNEL_LOAD_OPTIONS,
  GET_DEFAULT_CONVERSION_FUNNEL,
} from "lib/constants";
import { enqueueSnackbar } from "notistack";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { getFilterDates } from "utils/HelperFunctions";
import "../../../../styles/performance.scss";
import CollapsibleList from "./CollapsibleList";
import ConversionFilterModal from "./ConversionFilterModal";
import RenameFunnelModal from "./RenameFunnelModal";
import AccessListModal from "./AccessListModal";
import SaveAsModal from "./SaveAsModal";
import LoadModal from "./LoadModal";
import ConversionFunnelOptions from "./ConversionFunnelOptions";
import FunnelTier from "./FunnelTier";
import UserList from "./UserList";

function ConversionFunnel({ conversionFunnelId }) {
  const defaultTiers = [
    { id: "0", kpis: [] },
    { id: "1", kpis: [] },
  ];
  const defaultFilters = {
    dateFilter: "This Week",
    location: [],
    team: [],
    user: [],
    role: [],
    reportsTo: [],
  };
  const userConfigName =
    conversionFunnelId === 1
      ? "DEFAULT_CONVERSION_FUNNEL"
      : `DEFAULT_CONVERSION_FUNNEL_${conversionFunnelId}`;
  const loginUser = useSelector((state) => state.loginUser.loginUserState);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false);
  const [isAccessListModalOpen, setIsAccessListModalOpen] = useState(false);
  const [isLoadModalOpen, setIsLoadModalOpen] = useState(false);

  const [funnel, setFunnel] = useState({ tiers: defaultTiers, filters: defaultFilters });
  const [loadedFunnel, setLoadedFunnel] = useState();
  const [kpis, setKpis] = useState({ active: [], inactive: [] });
  const [tierTotals, setTierTotals] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(0);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [title, setTitle] = useState("New Funnel");
  const [accessList, setAccessList] = useState([loginUser.userCompanyLinkId]);
  const [accessListOptions, setAccessListOptions] = useState([]);
  const [loadOptions, setLoadOptions] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const prevTiers = useRef(defaultTiers);

  const showNotification = (type, message) => {
    enqueueSnackbar(
      JSON.stringify({
        icon: type,
        title: type === "success" ? "Success" : "Error",
        text: message,
      }),
      {
        variant: "custom",
      }
    );
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: window.innerWidth < 400 ? "100%" : 550,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
    borderRadius: "0.5rem",
  };

  const setFilters = (filters) => {
    setFunnel({ ...funnel, filters });
  };

  const setTiers = (tiers) => {
    setFunnel({ ...funnel, tiers });
  };

  const loadFunnel = async (funnelId) => {
    try {
      let loaded = false;
      const result = await getAPICall(GET_FUNNEL_BY_ID, true, {
        funnelId,
      });
      if (result?.data?.success) {
        const record = result.data.data;
        const newFunnel = JSON.parse(record.funnel);
        const newAccessList = record.ConversionFunnelsAccessLists.map(
          (access) => access.FK_user_companies_links_id
        );

        setAccessList(newAccessList);
        setTitle(record.title);
        setLoadedFunnel(record);
        setFunnel(newFunnel);

        loaded = true;
      }
      return loaded;
    } catch (error) {
      return false;
    }
  };

  const loadDefaultFunnel = async () => {
    try {
      let loaded = false;
      const result = await getAPICall(GET_DEFAULT_CONVERSION_FUNNEL, true, {
        userConfigName,
      });
      if (result?.data?.success) {
        const record = result.data.data;
        if (record) {
          const newFunnel = JSON.parse(record.funnel);
          const newAccessList = record.ConversionFunnelsAccessLists.map(
            (access) => access.FK_user_companies_links_id
          );

          setAccessList(newAccessList);
          setTitle(record.title);
          setLoadedFunnel(record);
          setFunnel(newFunnel);

          loaded = true;
        }
      }
      return loaded;
    } catch (error) {
      return false;
    }
  };

  const fetchFunnelData = async () => {
    const dateRange = getFilterDates(
      funnel.filters.dateFilter,
      loginUser.timezone,
      loginUser.fiscalYearStart
    );
    const result = await postAPICall(FETCH_FUNNEL_DATA, true, {
      filters: funnel.filters,
      tiers: funnel.tiers,
      dateRange,
    });
    if (result?.data?.success) {
      setKpis(result.data.data.kpis);
      setTierTotals(result.data.data.tierTotals);
      setFilteredUsers(result.data.data.filteredUsers);
      if (result.data.data.filteredUsers.length === 1) {
        setSelectedUserId(result.data.data.filteredUsers[0].id);
      }
    }
  };

  const handleDeleteTier = (id) => {
    const index = funnel.tiers.findIndex((t) => t.id === id);
    if (index >= 0) {
      const newTiers = [...funnel.tiers];
      newTiers.splice(index, 1);
      setTiers(newTiers);
    }
  };

  const handleRemoveKpi = (tierId, kpiId) => {
    setTiers(
      funnel.tiers.map((tier) =>
        tier.id === tierId ? { ...tier, kpis: tier.kpis.filter((kpi) => kpi.id !== kpiId) } : tier
      )
    );
  };

  const handleAddTier = () => {
    const maxValue = funnel.tiers.reduce((max, obj) => (+obj.id > max ? +obj.id : max), 0);
    const newTier = { id: String(maxValue + 1), kpis: [] };
    setTiers([...funnel.tiers, newTier]);
  };

  const handleUserClick = (userId) => {
    setSelectedUserId(userId);
  };

  const handleSave = async () => {
    if (!loadedFunnel) {
      showNotification("error", "Unable to save, no funnel is loaded");
      return;
    }
    if (
      loadedFunnel &&
      loadedFunnel.FK_Owner_user_companies_links_id !== loginUser.userCompanyLinkId
    ) {
      showNotification("error", "Only onwer has permissions to save changes");
      return;
    }

    const response = await postAPICall(SAVE_CONVERSION_FUNNEL, true, {
      funnelId: loadedFunnel.id,
      funnel: JSON.stringify(funnel),
    });
    if (response?.data?.success) {
      showNotification("success", "Saved successfully!");
    } else {
      showNotification("error", "Something went wrong");
    }
  };

  const handleSaveAs = async (incomingTitle, incomingAccessList, userFunnel = true) => {
    const response = await postAPICall(SAVE_CONVERSION_FUNNEL_AS, true, {
      title: incomingTitle,
      userId: userFunnel ? loginUser.userCompanyLinkId : null,
      accessList: incomingAccessList,
      funnel: JSON.stringify(funnel),
    });
    if (response?.data?.success) {
      setTitle(incomingTitle);
      setAccessList(incomingAccessList);
      setLoadedFunnel(JSON.parse(JSON.stringify(response.data.data.funnel)));
      showNotification("success", `Saved ${incomingTitle}!`);
    } else {
      showNotification("error", "Something went wrong");
    }
  };

  const handleSaveAsModalOpen = async () => {
    if (!accessListOptions.length) {
      // fetch access list options
      const result = await getAPICall(GET_FUNNEL_ACCESS_LIST_OPTIONS, true);
      if (result?.data?.success) {
        setAccessListOptions(result.data.data.userList);
      }
    }
    setIsSaveAsModalOpen(true);
  };

  const handleLoadModalOpen = async () => {
    if (!loadOptions.length) {
      const result = await getAPICall(GET_FUNNEL_LOAD_OPTIONS, true);
      if (result?.data?.success) {
        setLoadOptions(result.data.data.funnelList);
      }
    }
    setIsLoadModalOpen(true);
  };

  const handleAccessListModalOpen = async () => {
    if (!accessListOptions.length) {
      // fetch access list options
      const result = await getAPICall(GET_FUNNEL_ACCESS_LIST_OPTIONS, true);
      if (result?.data?.success) {
        setAccessListOptions(result.data.data.userList);
      }
    }
    setIsAccessListModalOpen(true);
  };

  const handleLoad = async (funnelId) => {
    const loaded = await loadFunnel(funnelId);
    if (!loaded) {
      showNotification("error", "Unable to load funnel!");
    }
  };

  const handleCreateNew = () => {
    setTiers(defaultTiers);
    setTitle("New Funnel");
    setAccessList([loginUser.userCompanyLinkId]);
    setLoadedFunnel(null);
  };

  const handleChangeAccessList = async (newAccessList) => {
    const response = await postAPICall(UPDATE_CONVERSION_FUNNEL_ACCESS_LIST, true, {
      funnelId: loadedFunnel.id,
      accessList: newAccessList,
    });
    if (response?.data?.success) {
      setAccessList(newAccessList);
      showNotification("success", "Access List updated!");
    } else {
      showNotification("error", response?.response?.data?.message || "Something went wrong");
    }
  };

  const handleRename = async (incomingTitle) => {
    // Funnel isn't saved yet, just set title
    if (!loadedFunnel) {
      setTitle(incomingTitle);
      return;
    }

    // Update record with new title
    const payload = {
      title: incomingTitle,
    };
    const response = await putAPICall(UPDATE_CONVERSION_FUNNEL, true, {
      funnelId: loadedFunnel.id,
      payload,
    });
    if (response?.data?.success) {
      setTitle(incomingTitle);
      showNotification("success", `Renamed ${incomingTitle}!`);
    } else {
      showNotification("error", response?.response?.data?.message || "Something went wrong");
    }
  };

  const handleSetAsDefault = async () => {
    if (!loadedFunnel) {
      showNotification("error", "Unable to save, no funnel is loaded");
      return;
    }
    const response = await postAPICall(`${USER_CONFIGS}/${loginUser.userCompanyLinkId}`, true, {
      value: loadedFunnel.id,
      name: userConfigName,
    });
    if (response?.data?.success) {
      showNotification(
        "success",
        response?.data?.message || response.message || "Default conversion funnel saved!"
      );
    } else {
      showNotification("error", "Something went wrong");
    }
  };

  const handleClearDefault = async () => {
    const response = await deleteAPICall(`${USER_CONFIGS}/${loginUser.userCompanyLinkId}`, true, {
      name: userConfigName,
    });
    if (response?.data?.success) {
      showNotification(
        "success",
        response?.data?.message || response.message || "Default conversion funnel removed!"
      );
    } else {
      showNotification("error", response?.response?.data?.message || "Something went wrong");
    }
  };

  const displayFilters = () => {
    const components = [];
    Object.keys(funnel.filters).forEach((key) => {
      if (Array.isArray(funnel.filters[key]) && !funnel.filters[key].length) return;
      let keyDisplay = key;
      if (key === "reportsTo") {
        keyDisplay = "Reports To";
      } else if (key === "dateFilter") {
        keyDisplay = "Date Filter";
      }

      let valueDisplay = funnel.filters[key];
      if (Array.isArray(funnel.filters[key])) {
        valueDisplay = funnel.filters[key]?.map(
          (appliedFilter, inx) =>
            `${appliedFilter.label} ${inx !== funnel.filters[key].length - 1 ? "," : ""} `
        );
      }
      components.push(
        <React.Fragment key={key}>
          <MDTypography
            variant="body1"
            sx={{
              textTransform: "capitalize",
              mr: 1,
              color: "#4c4c4c",
              fontSize: "0.9rem",
              lineHeight: "1.2",
            }}
          >
            {keyDisplay}
          </MDTypography>
          <MDTypography
            variant="body1"
            sx={{
              fontStyle: "italic",
              color: "#7882a6",
              fontSize: "0.8rem",
              mr: 2,
              lineHeight: "1.2",
            }}
          >
            {valueDisplay}
          </MDTypography>
        </React.Fragment>
      );
    });
    return components;
  };

  const onDragEnd = (result) => {
    try {
      const { destination, draggableId } = result;

      if (!destination) return;

      let droppedKpi = kpis.active.find((kpi) => kpi.id === +draggableId);
      if (droppedKpi) {
        const newList = kpis.active.filter((kpi) => kpi.id !== droppedKpi.id);
        setKpis({ ...kpis, active: newList });
      } else {
        droppedKpi = kpis.inactive.find((kpi) => kpi.id === +draggableId);
        const newList = kpis.inactive.filter((kpi) => kpi.id !== droppedKpi.id);
        setKpis({ ...kpis, inactive: newList });
      }
      const targetTier = funnel.tiers.find((tier) => tier.id === destination.droppableId);
      const updatedTiers = funnel.tiers.map((tier) =>
        tier.id === destination.droppableId
          ? { ...tier, kpis: [...targetTier.kpis, droppedKpi] }
          : tier
      );

      setTiers(updatedTiers);
    } catch (error) {
      showNotification("error", "Could not add KPI!");
    }
  };

  const getDefaultFunnel = async () => {
    await loadDefaultFunnel();
  };

  useEffect(() => {
    // Don't refetch data on tier add
    if (isLoaded) {
      if (JSON.stringify(funnel.tiers.slice(0, -1)) !== JSON.stringify(prevTiers.current)) {
        fetchFunnelData();
      }
      prevTiers.current = funnel.tiers;
    }
  }, [funnel, isLoaded]);

  useEffect(() => {
    getDefaultFunnel();
    setIsLoaded(true);
  }, []);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Box height="100%">
        <Grid container direction="column" height="100%">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ flex: 1, textAlign: "center" }}>
              <MDTypography
                variant="h3"
                sx={{
                  textAlign: "center",
                }}
              >
                Conversion Funnel
              </MDTypography>
              <MDTypography
                variant="h6"
                sx={{
                  textAlign: "center",
                  fontWeight: "400",
                }}
              >
                {title}
              </MDTypography>
            </Box>
            <Box>
              <ConversionFunnelOptions
                existing={!!loadedFunnel}
                isOwner={
                  !loadedFunnel ||
                  loadedFunnel.FK_Owner_user_companies_links_id === loginUser.userCompanyLinkId
                }
                handleCreateNew={handleCreateNew}
                handleRename={() => setIsRenameModalOpen(true)}
                handleSaveAs={handleSaveAsModalOpen}
                handleSave={loadedFunnel ? handleSave : handleSaveAsModalOpen}
                handleSetAsDefault={handleSetAsDefault}
                handleClearDefault={handleClearDefault}
                handleChangeAccessList={handleAccessListModalOpen}
                handleLoad={handleLoadModalOpen}
              />
            </Box>
          </Box>
          <Grid item height="500px">
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              flexWrap="noWrap"
              height="10%"
            >
              <Grid item xs={3} md={2}>
                <MDButton
                  sx={{ minWidth: "100%", borderRadius: "2rem" }}
                  color="primaryRed"
                  onClick={() => setIsFilterModalOpen(true)}
                >
                  Filter
                </MDButton>
              </Grid>
              <Grid item sx={{ ml: "1rem" }}>
                <Box
                  sx={{ textTransform: "capitalize", alignItems: "flex-end", flexWrap: "wrap" }}
                  display="inline-flex"
                >
                  {displayFilters()}
                </Box>
              </Grid>
            </Grid>

            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              height="90%"
            >
              <Grid item xs={8} height="100%" sx={{ overflowY: "auto" }}>
                {funnel.tiers.map((tier, index) => (
                  <Droppable key={tier.id} droppableId={`${tier.id}`}>
                    {(provided, snapshot) => (
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        <Box
                          className="conversion-tier-card"
                          sx={{
                            ...(snapshot.isDraggingOver
                              ? {
                                  border: "2px solid cornflowerblue",
                                }
                              : {}),
                          }}
                        >
                          <FunnelTier
                            tier={tier}
                            index={index}
                            totals={tierTotals[selectedUserId] || {}}
                            handleDeleteTier={handleDeleteTier}
                            handleRemoveKpi={handleRemoveKpi}
                          />
                        </Box>
                      </div>
                    )}
                  </Droppable>
                ))}
                <Button
                  type="submit"
                  color="primaryRed"
                  sx={{ float: "right" }}
                  onClick={handleAddTier}
                >
                  + Add New Tier
                </Button>
              </Grid>
              <Grid item xs={4} height="100%" sx={{ borderLeft: "1px dotted #ccc" }}>
                <Box sx={{ height: "35%", borderBottom: "1px dotted #ccc" }}>
                  <UserList
                    users={filteredUsers}
                    selectedUserId={selectedUserId}
                    handleUserClick={handleUserClick}
                  />
                </Box>
                <Box sx={{ height: "65%", overflowY: "auto" }}>
                  <CollapsibleList header="Active KPIs" list={kpis.active} expanded />
                  <CollapsibleList header="Inactive KPIs" list={kpis.inactive} />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      {isFilterModalOpen && (
        <ConversionFilterModal
          style={modalStyle}
          open={isFilterModalOpen}
          setFilters={setFilters}
          filters={funnel.filters}
          handleClose={() => setIsFilterModalOpen((prev) => !prev)}
        />
      )}
      {isRenameModalOpen && (
        <RenameFunnelModal
          style={modalStyle}
          open={isRenameModalOpen}
          title={title}
          handleRename={handleRename}
          handleClose={() => setIsRenameModalOpen((prev) => !prev)}
        />
      )}
      {isAccessListModalOpen && (
        <AccessListModal
          style={modalStyle}
          open={isAccessListModalOpen}
          ownerId={loginUser.userCompanyLinkId}
          accessList={accessList}
          accessListOptions={accessListOptions}
          handleChangeAccessList={handleChangeAccessList}
          handleClose={() => setIsAccessListModalOpen((prev) => !prev)}
        />
      )}
      {isLoadModalOpen && (
        <LoadModal
          style={modalStyle}
          userCompanyLinkId={loginUser.userCompanyLinkId}
          open={isLoadModalOpen}
          loadOptions={loadOptions}
          handleLoad={handleLoad}
          handleClose={() => setIsLoadModalOpen((prev) => !prev)}
        />
      )}
      {isSaveAsModalOpen && (
        <SaveAsModal
          style={modalStyle}
          open={isSaveAsModalOpen}
          title={loadedFunnel ? `Copy of ${title}` : title}
          ownerId={loginUser.userCompanyLinkId}
          accessList={accessList}
          accessListOptions={accessListOptions}
          header={loadedFunnel ? "Make a Copy" : "Save New Funnel"}
          handleSaveAs={handleSaveAs}
          handleClose={() => setIsSaveAsModalOpen((prev) => !prev)}
        />
      )}
    </DragDropContext>
  );
}

export default ConversionFunnel;
